/**
 * Cúntóirí Management Page
 * Coláiste Lurgan Management System - Upload and manage summer supervisors
 */

import React, { useState, useRef, useEffect } from 'react';
import Papa from 'papaparse';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import { Plus, Upload, Pencil, Trash2, Loader2, AlertCircle, FileText, X, ArrowRight, CheckCircle2, RotateCcw, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function GenderBadge({ gender }) {
  return gender === 'M' ? (
    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">
      M
    </Badge>
  ) : (
    <Badge variant="outline" className="bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300 dark:border-pink-700">
      F
    </Badge>
  );
}

// ─── Add/Edit Modal ────────────────────────────────────────────────────────────

const EMPTY_FORM = { first_name: '', last_name: '', email: '', gender: 'M', course: '' };

function CuntoirModal({ open, onClose, onSave, initial, courses, isSaving }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);

  React.useEffect(() => {
    if (open) {
      setForm(initial
        ? {
            first_name: initial.first_name ?? '',
            last_name: initial.last_name ?? '',
            email: initial.email ?? '',
            gender: initial.gender ?? 'M',
            course: initial.course ?? '',
          }
        : EMPTY_FORM
      );
    }
  }, [open, initial]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim(),
      gender: form.gender,
      course: form.course || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {initial ? t('cuntoirs.updated') : t('cuntoirs.addCuntoir')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t('profile.firstName')} *</Label>
              <Input value={form.first_name} onChange={set('first_name')} required />
            </div>
            <div className="space-y-1">
              <Label>{t('profile.lastName')} *</Label>
              <Input value={form.last_name} onChange={set('last_name')} required />
            </div>
          </div>
          <div className="space-y-1">
            <Label>{t('cuntoirs.email')}</Label>
            <Input type="email" value={form.email} onChange={set('email')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t('cuntoirs.gender')} *</Label>
              <Select value={form.gender} onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">{t('gender.M')}</SelectItem>
                  <SelectItem value="F">{t('gender.F')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>{t('cuntoirs.course')}</Label>
              <Select
                value={form.course || '__none__'}
                onValueChange={(v) => setForm((f) => ({ ...f, course: v === '__none__' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('cuntoirs.selectCourse')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('common.none', 'None')}</SelectItem>
                  {courses?.map((c) => (
                    <SelectItem key={c.uuid} value={c.uuid}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Cúntóir field definitions for mapping ────────────────────────────────────

const CUNTOIR_FIELDS = [
  { key: 'first_name', required: true,  variations: ['first name', 'first_name', 'firstname', 'forename', 'ainm'] },
  { key: 'last_name',  required: true,  variations: ['last name', 'last_name', 'lastname', 'surname', 'sloinne'] },
  { key: 'gender',     required: true,  variations: ['gender', 'sex', 'inscne'] },
  { key: 'email',      required: false, variations: ['email', 'e-mail', 'ríomhphost'] },
  { key: 'course',     required: false, variations: ['course', 'cúrsa', 'session'] },
];

const CUNTOIR_REQUIRED = CUNTOIR_FIELDS.filter(f => f.required).map(f => f.key);

function autoDetectCuntoirMapping(columns) {
  const result = {};
  columns.forEach(col => {
    const norm = col.toLowerCase().trim();
    for (const field of CUNTOIR_FIELDS) {
      if (field.variations.some(v => norm.includes(v))) {
        result[col] = field.key;
        break;
      }
    }
  });
  return result;
}

// ─── CSV Upload Modal (2-step: file → mapping) ────────────────────────────────

function CsvUploadModal({ open, onClose, onUpload, isUploading }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [step, setStep] = useState(1); // 1 = file, 2 = mapping
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  // Reset when closed
  useEffect(() => {
    if (!open) {
      setTimeout(() => { setStep(1); setFile(null); setColumns([]); setMapping({}); }, 300);
    }
  }, [open]);

  const handleClose = () => onClose();

  const parseColumns = (f) => {
    Papa.parse(f, {
      preview: 1,
      header: true,
      transformHeader: (h) => h.replace(/^\uFEFF/, '').trim(),
      complete: (results) => {
        const cols = results.meta.fields ?? [];
        setColumns(cols);
        setMapping(autoDetectCuntoirMapping(cols));
      },
    });
  };

  const handleFile = (f) => {
    if (!f || !f.name.endsWith('.csv')) return;
    setFile(f);
    parseColumns(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleMappingChange = (col, fieldKey) => {
    setMapping(prev => {
      const next = { ...prev };
      if (fieldKey === 'IGNORE') delete next[col];
      else next[col] = fieldKey;
      return next;
    });
  };

  const mappedFields = Object.values(mapping);
  const allRequiredMapped = CUNTOIR_REQUIRED.every(f => mappedFields.includes(f));
  const requiredMappedCount = CUNTOIR_REQUIRED.filter(f => mappedFields.includes(f)).length;

  const handleSubmit = () => {
    if (!file || !allRequiredMapped) return;
    onUpload(file, mapping);
  };

  const STEPS = [
    { id: 1, key: 'upload',  icon: Upload },
    { id: 2, key: 'mapping', icon: MapPin },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{t('cuntoirs.uploadCsv')}</DialogTitle>

          {/* Step indicator — mirrors BulkUploadModal exactly */}
          <div className="mt-6">
            <div className="flex items-center">
              {STEPS.map((s, idx) => {
                const Icon = s.icon;
                const isActive = s.id === step;
                const isCompleted = s.id < step;
                return (
                  <React.Fragment key={s.id}>
                    <div className="flex flex-col items-center flex-1">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center transition-colors
                        ${isActive    ? 'bg-blue-600 text-white dark:bg-blue-500' : ''}
                        ${isCompleted ? 'bg-green-600 text-white dark:bg-green-500' : ''}
                        ${!isActive && !isCompleted ? 'bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500' : ''}
                      `}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className={`text-xs mt-2 text-center ${isActive ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                        {t(`bulkUpload.steps.${s.key}.title`)}
                      </span>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div className={`flex-1 h-0.5 -mt-8 ${s.id < step ? 'bg-green-600 dark:bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </DialogHeader>

        {/* Step content */}
        <div className="flex-1 py-6 overflow-y-auto space-y-4">

          {/* ── Step 1: File ── */}
          {step === 1 && (
            <>
              {!file && (
                <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                    {t('cuntoirs.upload.instructions.title')}
                  </h3>
                  <ul className="list-disc list-inside text-sm text-blue-800 dark:text-blue-200 space-y-1">
                    <li>{t('cuntoirs.upload.instructions.required')}</li>
                    <li>{t('cuntoirs.upload.instructions.optional')}</li>
                    <li>{t('cuntoirs.upload.instructions.allCourses')}</li>
                    <li>{t('cuntoirs.upload.instructions.csvFormat')}</li>
                  </ul>
                </div>
              )}
              {!file ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onClick={() => fileRef.current?.click()}
                  className={`
                    border-2 border-dashed rounded-lg p-12
                    flex flex-col items-center justify-center
                    cursor-pointer transition-colors
                    ${isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20' : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'}
                  `}
                >
                  <Upload className={`w-16 h-16 mb-4 ${isDragging ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'}`} />
                  <p className="text-lg font-semibold mb-2 text-gray-700 dark:text-gray-200">{t('bulkUpload.steps.upload.dropzone.title', 'Drop CSV here or click to browse')}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('bulkUpload.steps.upload.dropzone.subtitle', '.csv files only')}</p>
                  <Button variant="outline" type="button">{t('bulkUpload.steps.upload.dropzone.button', 'Browse Files')}</Button>
                  <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
                </div>
              ) : (
                <div className="border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="bg-green-100 dark:bg-green-900/40 p-3 rounded-lg">
                        <FileText className="w-8 h-8 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-green-900 dark:text-green-100 mb-1">{file.name}</h4>
                        <p className="text-sm text-green-700 dark:text-green-300">{(file.size / 1024).toFixed(2)} KB · {columns.length} {t('bulkUpload.steps.mapping.stats.totalColumns', 'columns')}</p>
                      </div>
                    </div>
                    <Button type="button" variant="ghost" size="icon" className="text-green-700 dark:text-green-400 hover:text-green-900 dark:hover:text-green-200" onClick={() => { setFile(null); setColumns([]); setMapping({}); }}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Step 2: Mapping ── */}
          {step === 2 && (
            <div className="flex flex-col h-full gap-4">
              {/* Instructions */}
              <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg flex-shrink-0">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">{t('bulkUpload.steps.mapping.instructions.title')}</h3>
                <p className="text-sm text-blue-800 dark:text-blue-200">{t('bulkUpload.steps.mapping.instructions.description')}</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 flex-shrink-0">
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{columns.length}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{t('bulkUpload.steps.mapping.stats.totalColumns')}</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{mappedFields.length}</div>
                  <div className="text-sm text-blue-700 dark:text-blue-300">{t('bulkUpload.steps.mapping.stats.mapped')}</div>
                </div>
                <div className={`p-4 rounded-lg ${allRequiredMapped ? 'bg-green-50 dark:bg-green-950/30' : 'bg-red-50 dark:bg-red-950/30'}`}>
                  <div className={`text-2xl font-bold ${allRequiredMapped ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
                    {requiredMappedCount}/{CUNTOIR_REQUIRED.length}
                  </div>
                  <div className={`text-sm ${allRequiredMapped ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                    {t('bulkUpload.steps.mapping.stats.required')}
                  </div>
                </div>
              </div>

              {/* Mapping table */}
              <div className="border rounded-lg overflow-hidden flex-1 flex flex-col min-h-0">
                <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 border-b flex justify-between items-center flex-shrink-0">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('bulkUpload.steps.mapping.table.title')}</h3>
                  <Button variant="outline" size="sm" onClick={() => setMapping(autoDetectCuntoirMapping(columns))}>
                    <RotateCcw className="w-4 h-4 mr-2" />{t('bulkUpload.steps.mapping.resetButton')}
                  </Button>
                </div>
                <div className="divide-y overflow-y-auto min-h-0">
                  {columns.map((col) => {
                    const mapped = mapping[col];
                    const isRequired = CUNTOIR_REQUIRED.includes(mapped);
                    return (
                      <div key={col} className="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/30">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{col}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{t('bulkUpload.steps.mapping.table.csvColumn')}</div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-400 dark:text-gray-500 mx-4" />
                        <div className="flex-1 flex items-center gap-2">
                          <Select value={mapped || 'IGNORE'} onValueChange={(v) => handleMappingChange(col, v)}>
                            <SelectTrigger className={isRequired ? 'border-green-500 dark:border-green-500' : ''}>
                              <SelectValue placeholder={t('bulkUpload.steps.mapping.table.selectField')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="IGNORE">{t('bulkUpload.steps.mapping.table.ignore')}</SelectItem>
                              {CUNTOIR_FIELDS.map(f => (
                                <SelectItem key={f.key} value={f.key}>
                                  {t(`cuntoirs.fields.${f.key}`, f.key)}{f.required ? ' *' : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {mapped && mapped !== 'IGNORE' && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 p-0 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" onClick={() => handleMappingChange(col, 'IGNORE')}>
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <div className="ml-4 w-6">
                          {isRequired && <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between pt-4 border-t">
          {step === 1 ? (
            <Button variant="outline" onClick={handleClose}>{t('common.cancel')}</Button>
          ) : (
            <Button variant="outline" onClick={() => setStep(1)}>
              <ChevronLeft className="w-4 h-4 mr-2" />{t('common.back')}
            </Button>
          )}
          <div className="text-sm text-gray-500 dark:text-gray-400 self-center">
            {t('bulkUpload.stepProgress', { current: step, total: STEPS.length })}
          </div>
          {step === 1 && (
            <Button onClick={() => setStep(2)} disabled={!file} className={file ? 'bg-green-600 hover:bg-green-700' : ''}>
              {t('bulkUpload.steps.upload.next')} <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          {step === 2 && (
            <Button onClick={handleSubmit} disabled={!allRequiredMapped || isUploading} className={allRequiredMapped && !isUploading ? 'bg-green-600 hover:bg-green-700' : ''}>
              {isUploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('cuntoirs.uploadCsv')} <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export function CuntoiriManagement() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [editingCuntoir, setEditingCuntoir] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Data
  const { data: cuntoirsData, isLoading } = useQuery({
    queryKey: ['cuntoirs'],
    queryFn: () => api.cuntoirs.list(),
  });
  const cuntoirs = cuntoirsData?.results ?? cuntoirsData ?? [];

  const { data: coursesData } = useQuery({
    queryKey: ['courses'],
    queryFn: () => api.courses.getAll(),
  });
  const courses = coursesData?.results ?? coursesData ?? [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: api.cuntoirs.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['cuntoirs']);
      setModalOpen(false);
      toast({ title: t('cuntoirs.created'), variant: 'success' });
    },
    onError: () => toast({ title: t('common.error'), variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ uuid, data }) => api.cuntoirs.update(uuid, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['cuntoirs']);
      setModalOpen(false);
      setEditingCuntoir(null);
      toast({ title: t('cuntoirs.updated'), variant: 'success' });
    },
    onError: () => toast({ title: t('common.error'), variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: api.cuntoirs.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['cuntoirs']);
      setDeleteTarget(null);
      toast({ title: t('cuntoirs.deleted'), variant: 'success' });
    },
    onError: () => toast({ title: t('common.error'), variant: 'destructive' }),
  });

  const uploadMutation = useMutation({
    mutationFn: ({ file, mapping }) => api.cuntoirs.upload(file, mapping),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['cuntoirs']);
      setCsvOpen(false);
      toast({
        title: t('cuntoirs.uploadSuccess'),
        description: t('cuntoirs.uploadSuccessDesc', { count: data.created }),
        variant: 'success',
      });
    },
    onError: () => toast({ title: t('common.error'), variant: 'destructive' }),
  });

  const handleSave = (data) => {
    if (editingCuntoir) {
      updateMutation.mutate({ uuid: editingCuntoir.uuid, data });
    } else {
      createMutation.mutate(data);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('cuntoirs.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('cuntoirs.description')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCsvOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            {t('cuntoirs.uploadCsv')}
          </Button>
          <Button onClick={() => { setEditingCuntoir(null); setModalOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            {t('cuntoirs.addCuntoir')}
          </Button>
        </div>
      </div>

      {/* Table */}
      {cuntoirs.length === 0 ? (
        <div className="rounded-md border">
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <p className="text-sm">{t('cuntoirs.noData')}</p>
          </div>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>{t('cuntoirs.name')}</TableHead>
                <TableHead>{t('cuntoirs.email')}</TableHead>
                <TableHead className="w-16">{t('cuntoirs.gender')}</TableHead>
                <TableHead>{t('cuntoirs.course')}</TableHead>
                <TableHead>{t('cuntoirs.house')}</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {cuntoirs.map((c, idx) => (
                <TableRow key={c.uuid} className={idx % 2 === 1 ? 'bg-muted/20' : ''}>
                  <TableCell className="text-muted-foreground text-sm">{idx + 1}</TableCell>
                  <TableCell className="font-medium">
                    {c.full_name}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.email || '—'}</TableCell>
                  <TableCell><GenderBadge gender={c.gender} /></TableCell>
                  <TableCell className="text-sm">{c.course_name || '—'}</TableCell>
                  <TableCell className="text-sm">{c.house_name || <span className="text-muted-foreground">{t('cuntoirs.unassigned')}</span>}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => { setEditingCuntoir(c); setModalOpen(true); }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(c)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add/Edit Modal */}
      <CuntoirModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingCuntoir(null); }}
        onSave={handleSave}
        initial={editingCuntoir}
        courses={courses}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />

      {/* CSV Upload Modal */}
      <CsvUploadModal
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        onUpload={(file, mapping) => uploadMutation.mutate({ file, mapping })}
        isUploading={uploadMutation.isPending}
      />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              {t('common.confirm')}
            </DialogTitle>
            <DialogDescription>
              {t('cuntoirs.deleteConfirm')}
              {deleteTarget && <strong className="block mt-1">{deleteTarget.full_name}</strong>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(deleteTarget?.uuid)}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CuntoiriManagement;
